import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Button,
    IconButton,
    Chip,
    Box,
    Tooltip,
    Popover,
    Typography,
} from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useState } from "react";
import { useLazyListDeliveriesForDriverQuery } from "store/services/deliveryApi";

const DeliveryListScreenForDriver = () => {
    const { isVendor, isDriver, vendorId, isCustomer, loggedInUser } = useAuth();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();
    const [searchParams] = useSearchParams();
    const keyword = searchParams.get("search") || "";
    const breadcrumbs = [{ title: "Deliveries" }];
    const columns = [
        {
            name: "ID",
            selector: (row) => row.fulfillmentCompletionId,
            cell: (row) => (
                <Link
                    title={row?.fulfillmentCompletionId}
                    to={
                        !isDriver
                            ? `/app/crm/delivery/${row?._id}/detail`
                            : `/app/driver/delivery/${row?._id}/detail`
                    }
                    className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                >
                    {row?.fulfillmentCompletionId ? (
                        <HighlightedText
                            text={row?.fulfillmentCompletionId}
                            highlight={keyword}
                        />
                    ) : (
                        "N/A"
                    )}
                </Link>
            ),
        },
        ...(!isDriver
            ? [
                {
                    name: "Delivery Type",
                    selector: (row) =>
                        row?.responsibleUser?.name
                            ? "Branch Delivery"
                            : row?.sendOut?.sendOutId
                                ? `Home Delivery (${row.sendOut.sendOutId})`
                                : "N/A",
                    cell: (row) => {
                        if (row?.responsibleUser?.name) {
                            return "Branch Delivery";
                        }
                        if (row?.sendOut?.sendOutId) {
                            return !isCustomer && !isVendor && !isDriver ? (
                                <Link
                                    to={`/app/crm/sendOut/${row?.sendOut?._id}/detail`}
                                    className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                                >
                                    <HighlightedText
                                        text={`Home Delivery (${row.sendOut.sendOutId})`}
                                        highlight={keyword}
                                    />
                                </Link>
                            ) : (
                                <HighlightedText
                                    text={`Home Delivery (${row.sendOut.sendOutId})`}
                                    highlight={keyword}
                                />
                            );
                        }
                        return "N/A";
                    },
                },
            ]
            : []),
        {
            name: "Shipments",
            selector: (row) => row.shipmentId,
            cell: (row) => {
                const formatShipmentId = (shipmentId) =>
                    shipmentId?.replace?.(/(.{4})/g, "$1-").slice(0, -1) || shipmentId || "N/A";

                // eslint-disable-next-line react-hooks/rules-of-hooks
                const [anchorEl, setAnchorEl] = useState(null);
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const [selectedShipments, setSelectedShipments] = useState([]);

                // Get shipment IDs from shipmentId array
                const shipmentIds = Array.isArray(row.shipmentId) ? row.shipmentId : [];

                // Get shipment details from shipmentrequest array
                const shipmentDetails = Array.isArray(row.shipmentrequest) ? row.shipmentrequest : [];

                // Combine them to get full shipment info
                const shipments = shipmentIds.map(id => {
                    const detail = shipmentDetails.find(d => d._id === id) || {};
                    return {
                        shipmentId: detail.shipmentId || id, // fallback to raw ID if no detail found
                        IsReturning: detail.IsReturning || false
                    };
                });

                const visibleShipments = shipments.slice(0, 2);
                const remainingCount = shipments.length - visibleShipments.length;

                const handleChipClick = (event, allShipments) => {
                    setAnchorEl(event.currentTarget);
                    setSelectedShipments(allShipments);
                };

                const handleClose = () => {
                    setAnchorEl(null);
                };

                const open = Boolean(anchorEl);

                return shipments.length > 0 ? (
                    <>
                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                            {visibleShipments.map((shipment, index) => (
                                <span key={index} style={{ fontSize: "0.9rem" }}>
                                    {shipment.IsReturning
                                        ? `${formatShipmentId(shipment.shipmentId)} (R)`
                                        : formatShipmentId(shipment.shipmentId)}
                                    {index !== visibleShipments.length - 1 && ","}
                                </span>
                            ))}

                            {remainingCount > 0 && (
                                <Tooltip title="Click to view all Shipment IDs">
                                    <Chip
                                        label={`+${remainingCount}`}
                                        size="small"
                                        sx={{
                                            marginLeft: "4px",
                                            height: "18px",
                                            fontSize: "10px",
                                            backgroundColor: "#e0e0e0",
                                            color: "#333",
                                            cursor: "pointer",
                                        }}
                                        onClick={(e) => handleChipClick(e, shipments)}
                                    />
                                </Tooltip>
                            )}
                        </Box>

                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                        >
                            <Box p={2} minWidth={100}>
                                {selectedShipments.map((shipment, index) => (
                                    <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{ wordBreak: "break-word" }}
                                    >
                                        {shipment.IsReturning
                                            ? `${formatShipmentId(shipment.shipmentId)} (R)`
                                            : formatShipmentId(shipment.shipmentId)}
                                    </Typography>
                                ))}
                            </Box>
                        </Popover>
                    </>
                ) : (
                    "N/A"
                );
            },
        },
        {
            name: "Actual Delivery Time",
            selector: (row) => <AppTableDateTime date={row?.actualDeliveryTime} />,
        },
        {
            name: "Created By",
            selector: (row) => row.createdBy?.name || "N/A",
            cell: (row) => row.createdBy?.name || "N/A",
        },
        {
            name: "Created At",
            selector: (row) => <AppTableDateTime date={row?.createdAt} />,
        },
        {
            name: "Actions",
            cell: (row) => {
                const basePath = isVendor
                    ? "/app/vendor/delivery"
                    : isCustomer
                        ? "/app/customer/delivery"
                        : "/app/crm/delivery";
                const isEditDisabled = isVendor && row?.status !== "Pending";

                return (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                    >
                        <Link
                            to={
                                !isDriver
                                    ? `${basePath}/${row?._id}/detail`
                                    : `/app/driver/delivery/${row?._id}/detail`
                            }
                        >
                            <IconButton
                                size="small"
                                aria-label="View Delivery"
                                color="primary"
                            >
                                <InfoOutlined />
                            </IconButton>
                        </Link>
                        {!isCustomer && !isDriver && (
                            <Link
                                to={isEditDisabled ? "#" : `${basePath}/${row?._id}/edit`}
                                style={{ pointerEvents: isEditDisabled ? "none" : "auto" }}
                            >
                                <IconButton
                                    size="small"
                                    aria-label="Edit Delivery"
                                    disabled={isEditDisabled}
                                    sx={{ opacity: isEditDisabled ? 0.5 : 1 }}
                                >
                                    <ModeEditOutlineTwoTone />
                                </IconButton>
                            </Link>
                        )}
                    </Box>
                );
            },
            ignoreRowClick: true,
            right: true,
            width: "120px",
        },
    ];

    const [fetchDeliveries, { data: deliveryRes, isLoading, isFetching, error }] =
        useLazyListDeliveriesForDriverQuery();

    React.useEffect(() => {
        fetchDeliveries({
            active: true,
            page: currentPage,
            driverId: loggedInUser.typeRef,
            limit: perPage,
            ...(keyword && { keyword }),
            ...(isVendor && { vendorId }),
        });
    }, [
        currentPage,
        fetchDeliveries,
        keyword,
        perPage,
        isVendor,
        vendorId,
        loggedInUser,
    ]);

    useAppMeta(` Deliveries`);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <TableFilter
                    searchLabel="Delivery ID/Shipment ID"
                    disabled={isLoading || isFetching}
                />
                <AppTable
                    columns={columns}
                    data={deliveryRes?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={deliveryRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default DeliveryListScreenForDriver;

const PageActions = () => {
    const { isDriver } = useAuth();

    return (
        <>
            <div className="mb-2 flex items-center gap-2">
                {!isDriver && (
                    <Link
                        to={
                            isDriver
                                ? "/app/driver/delivery/branch/delivery"
                                : "/app/crm/delivery/branch/delivery"
                        }
                    >
                        <Button startIcon={<Add />} variant="outlined" color="primary">
                            Branch Delivery
                        </Button>
                    </Link>
                )}

                <Link
                    to={
                        isDriver
                            ? "/app/driver/delivery/create"
                            : "/app/crm/delivery/create"
                    }
                >
                    <Button startIcon={<Add />} variant="contained" color="primary">
                        Door Delivery
                    </Button>
                </Link>
            </div>
        </>
    );
};